import { ReactNode } from 'react';
import { useLogger, useServices } from '@packages/shared/core/common/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { MultiAnswerStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';

type MultiAnswerQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const MultiAnswerQuizStep = ({ id, renderQuizScreen }: MultiAnswerQuizStepProps) => {
  const logger = useLogger();
  const { back, next, stepData, updateUserPreferences, userPreferences, totalSteps, stepOrder } = useQuizStep(id);
  const { analyticsService } = useServices();

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const multiAnswerData = stepData as MultiAnswerStepData;

  const { dataKey, otherOpenEvent, otherClickEvent } = multiAnswerData;

  const initialValue = Array.isArray(userPreferences[dataKey]) ? (userPreferences[dataKey] as Array<string>) : [];

  const goBack = async () => back();

  const onChosen = async (value: Array<string>) => {
    updateUserPreferences(dataKey, value);
    await next();
  };

  const goNext = async (value: Array<string>) => {
    if (multiAnswerData.analytic && multiAnswerData.analytic.answerEvent) {
      analyticsService.trackQuizEvent(multiAnswerData.analytic.answerEvent, {
        options: value,
      });
    }
    onChosen(value);
  };

  const onCustomOpen = () => {
    if (otherOpenEvent) {
      analyticsService.trackQuizEvent(otherOpenEvent, {});
    }
  };

  const onCustomChosen = (customAnswerText: string) => {
    if (!otherClickEvent) {
      return;
    }
    analyticsService.trackQuizEvent(otherClickEvent, {
      context: customAnswerText,
    });
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        initialValue,
        onChosen,
        onCustomOpen,
        onCustomChosen,
        goNext,
        totalSteps,
        stepOrder,
        ...multiAnswerData,
      })}
    </>
  );
};

export default MultiAnswerQuizStep;
